
import { Component, Provide, Mixins, Prop } from "vue-property-decorator";
import { FilteringSortingTableStore } from "@/mixins/tableWithFilteringAndSortingStore";
import { ITableConfig } from "@/models/tables";
import { DataModules } from "@/store/modules/data";
import {
  INote,
  INoteForm,
  INotesProviderData,
  INotesProviderMethods
} from "@/models/notes";
import { CreateElement } from "vue";
import { ActionTypes, ConfirmModalModes } from "@/data/enums";

@Component
export default class NotesProvider extends Mixins(FilteringSortingTableStore) {
  // Props

  @Prop({ required: true })
  public canEdit!: boolean;

  @Prop({ required: true })
  public canCreate!: boolean;

  @Prop({ required: true })
  public canDelete!: boolean;

  @Prop({ required: true })
  public canList!: boolean;

  @Prop({ required: true })
  public module!: DataModules;

  @Prop({ required: true })
  public payload!: Record<string, any>[];

  @Prop({ default: "NL" })
  public declare id: string;

  @Prop({ default: 3 })
  public declare limit: number;

  // Data

  public isProcessing = false;
  public defaults: ITableConfig = {
    page: 1,
    limit: this.limit,
    sort: {},
    filters: []
  };

  // Life-cycle Methods

  public render(h: CreateElement) {
    if (this.loading) return h("is-loading", { props: { if: true } });
    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $notesData: this.notesProviderData(),
        $notesMethods: this.notesProviderMethods
      })
    );
  }

  public created() {
    this.dataModule = this.module;
    this.getData();
  }

  // Methods

  public getStoreParams(params: any): Promise<any> {
    // Overrides mixin method
    return this.$_.merge(params, {
      ...this.payload,
      params: {
        with: "user",
        order: "-pinned,-updated_at"
      }
    });
  }

  public reloadData() {
    return this.getData({ ignoreStored: true });
  }

  public async createNote(data: INoteForm) {
    if (!this.canCreate) return;
    try {
      await this.$store.dispatch(`data/${this.dataModule}/create`, {
        ...this.payload,
        scope: this.dataScope,
        data
      });
      this.reloadData();
      this.$buefy.toast.open({
        message: this.$t("_.note_created") as string
      });
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    }
  }

  public editNote(note: INote) {
    if (!this.canEdit) return;
    if (this.isProcessing) return;
    this.$store.dispatch(`data/${this.dataModule}/openAddEditModal`, {
      parent: this,
      props: {
        action: ActionTypes.UPDATE,
        noteId: note.id,
        data: note,
        inModalContext: true
      }
    });
  }

  public async updateNote(noteId: INote["id"], data: INoteForm) {
    if (!this.canEdit) return;
    try {
      await this.$store.dispatch(`data/${this.dataModule}/update`, {
        ...this.payload,
        scope: this.dataScope,
        id: noteId,
        data
      });
      this.reloadData();
      this.$buefy.toast.open({
        message: this.$t("_.note_updated") as string
      });
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    }
  }

  public async pinNote(note: INote) {
    this.updateNote(note.id, { pinned: !note.pinned });
  }

  public async deleteNote(note: INote) {
    if (!this.canDelete) return;
    const confirm = await this.$store.dispatch("ui/open/confirmModal", {
      config: {
        props: {
          autoClose: false,
          mode: ConfirmModalModes.BUTTON,
          confirmButtonText: this.$t("_action.delete"),
          message: this.$t("_sentence.delete_confirmation", {
            object: (this.$t("_.note") as string).toLowerCase()
          })
        },
        events: {
          confirmed: async () => {
            await this.onDeleteConfirmed(note.id);
            confirm.close();
          }
        }
      }
    });
  }

  public async onDeleteConfirmed(noteId: INote["id"]): Promise<any> {
    try {
      await this.$store.dispatch(`data/${this.dataModule}/remove`, {
        ...this.payload,
        scope: this.dataScope,
        id: noteId
      });
      this.reloadData();
      this.$buefy.toast.open({
        message: this.$t("_.note_deleted") as string
      });
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    }
  }

  // Provide

  @Provide()
  public get notesProviderMethods(): INotesProviderMethods {
    return {
      createNote: this.createNote,
      deleteNote: this.deleteNote,
      editNote: this.editNote,
      onPageChange: this.onPageChange,
      pinNote: this.pinNote,
      reloadData: this.reloadData,
      updateNote: this.updateNote
    };
  }

  @Provide()
  public notesProviderData(): INotesProviderData {
    return {
      canDelete: this.canDelete,
      canEdit: this.canEdit,
      canList: this.canList,
      notes: this.table.data,
      total: this.table.total,
      page: this.table.page,
      limit: this.table.limit,
      isLoading: this.table.loading
    };
  }
}
